import { useEffect, useRef, useState } from "react";
import Spinner from "src/components/Spinner";
import useLoader from "src/hooks/useLoader";
import { downloadVideo, getInterviewVideoUrl } from "src/network/interview_video";
import { InterviewSection, InterviewVideo, JobInterview } from "src/types";
import { ReactComponent as Arrow } from "src/assets/svg/circle-arrow-left2.svg";
import { ReactComponent as DownloadIcon } from "src/assets/svg/download.svg";
import ReactPlayer from "react-player";
import { useAppSelector } from "src/redux/store";
import ConstraintModal from "src/components/Modal/ConstraintModal";
import Button from "src/components/Button";
const order: { [key in InterviewSection]: number } = {
  Introduction: 1,
  General: 2,
  Technical: 3,
  Personality: 4,
  End: 5
};

const VideosSection = ({
  videos,
  interview
}: {
  videos?: InterviewVideo[];
  interview: JobInterview;
}) => {
  const [constrainted, setConstrained] = useState(true);
  const enabled = useAppSelector(
    (r) => !!r.client.client?.Company.UsageConstraints?.video_recording_enabled
  );
  const ref = useRef(null);
  const [section, setSection] = useState<number>(0);
  const [videoUrl, setVideoUrl] = useState<string>("");
  if (!videos?.length) return null;
  const sections = videos.sort((a, b) => order[a.interview_section] - order[b.interview_section]);
  const fetchUrl = async () => {
    if (!enabled) return;
    const s = sections[section];
    setVideoUrl("");
    if (s.mp4_presigned_url) {
      setVideoUrl(s.mp4_presigned_url);
      return;
    }
    const url = await getInterviewVideoUrl({
      interview_id: interview.id,
      interview_section: s.interview_section
    });
    setVideoUrl(url);
  };
  const downloadV = async () => {
    if (!videoUrl) return;
    const s = sections[section];
    if (s.mp4_presigned_url) {
      window.open(s.mp4_presigned_url, "_blank");
      return;
    }
    await downloadVideo({
      interview_id: interview.id,
      interview_section: s.interview_section
    });
  };

  const { ldng, loading } = useLoader(fetchUrl, { errorMsg: "Error fetching video url" });
  const { ldng: download, loading: downloading } = useLoader(downloadV, {
    errorMsg: "Error downloading video"
  });

  useEffect(() => {
    ldng();
  }, [section]);

  return (
    <div className="mb-9">
      <div className="flex  items-center gap-5">
        <p className="font-poppins text-xl font-semibold">Video Recording</p>
      </div>
      <div className="flex mt-9 gap-[60px] items-center">
        <div className="h-[332px] w-[716px] p-6 bg-white rounded-xl border-[1px] border-[#ccc] flex items-center justify-center">
          {loading ? (
            <Spinner className="w-[50px] h-[50px]" />
          ) : (
            <ReactPlayer
              ref={ref}
              className="rounded-xl w-full h-[290px] !bg-black "
              url={videoUrl}
              width="100%"
              height="100%"
              controls
            />
          )}
        </div>

        <div>
          <p className="font-semibold text-xl text-center">{sections[section].interview_section}</p>
          <div className="flex gap-4 mx-auto w-fit my-10">
            <button
              disabled={loading}
              onClick={() => setSection(section ? section - 1 : sections.length - 1)}
            >
              <Arrow />
            </button>
            {sections.map((s, i) => {
              const current = section === i;
              return (
                <button
                  key={s.interview_section}
                  disabled={current || loading}
                  onClick={() => setSection(i)}
                  type="button"
                  className={
                    "font-poppins font-semibold text-xl " +
                    (current ? "text-odfblue" : "text-[#ccc] hover:text-odfblue-hue")
                  }
                >
                  0{i + 1}
                </button>
              );
            })}
            <button disabled={loading} onClick={() => setSection((section + 1) % sections.length)}>
              <Arrow className=" rotate-180" />
            </button>
          </div>
          <Button
            className="mx-auto min-w-[214px]"
            disabled={!videoUrl}
            onClick={download}
            loading={downloading}
            shapeVariant={2}
          >
            <DownloadIcon className="w-6 h-6 mr-2 stroke-white" /> Download Video
          </Button>
        </div>
      </div>

      <ConstraintModal
        variant="video_enabled"
        open={!enabled && constrainted}
        onClose={() => setConstrained(false)}
      />
    </div>
  );
};

export default VideosSection;
